import React, { useState } from 'react';
import { X, CreditCard } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssignCreditModal = ({ agent, onClose, onSuccess }) => {
  const caps = agent?.agent_payment_capabilities || {};
  const [formData, setFormData] = useState({
    credit_limit: '',
    credit_terms_days: '7',
    payment_day: 'MONDAY',
    transfer_wallet_to_credit: false,
    wallet_enabled: caps.wallet_enabled !== false,
    credit_enabled: caps.credit_enabled !== false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentWallet = agent?.wallet_balance || 0;
  const isHybrid = agent?.payment_mode === 'HYBRID';
  const hasCredit = agent?.payment_mode === 'CREDIT' || isHybrid || Number(agent?.credit_limit || 0) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        agent_id: agent.id,
        credit_limit: formData.credit_limit,
        credit_terms_days: formData.credit_terms_days,
        payment_day: formData.payment_day,
        transfer_wallet_to_credit: formData.transfer_wallet_to_credit,
        wallet_enabled: formData.wallet_enabled,
        credit_enabled: formData.credit_enabled,
      });

      await axios.post(`${API_URL}/api/admin/credit/assign?${params}`, null, {
        withCredentials: true
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign credit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Assign Credit</h2>
              <p className="text-sm text-gray-500">{agent?.full_name} ({agent?.agency_name})</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Current Payment Mode:</p>
          <p className="font-semibold text-gray-800">
            {isHybrid ? 'Hybrid (Wallet + Credit)' : hasCredit ? 'Credit' : 'Cash Wallet'}
          </p>
          <p className="text-sm text-gray-600 mt-2">
            Wallet: ₹{currentWallet.toLocaleString()}
            {hasCredit && (
              <> · Credit limit: ₹{Number(agent?.credit_limit || 0).toLocaleString()}</>
            )}
          </p>
          <p className="text-xs text-gray-500 mt-1">Payment gateway is always available at checkout.</p>
        </div>

        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
          <p className="text-sm font-semibold text-gray-800">Payment methods for this agent</p>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={formData.wallet_enabled}
              onChange={(e) => setFormData({ ...formData, wallet_enabled: e.target.checked })}
              className="w-4 h-4"
            />
            Allow cash wallet
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={formData.credit_enabled}
              onChange={(e) => setFormData({ ...formData, credit_enabled: e.target.checked })}
              className="w-4 h-4"
            />
            Allow credit line
          </label>
          <p className="text-xs text-gray-600">Payment gateway (Razorpay) — always on</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Credit Limit (₹) *
            </label>
            <input
              type="number"
              min="0"
              step="1000"
              required
              value={formData.credit_limit}
              onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter credit limit"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Credit Terms (days) *
            </label>
            <input
              type="number"
              min="1"
              max="90"
              required
              value={formData.credit_terms_days}
              onChange={(e) => setFormData({ ...formData, credit_terms_days: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="7"
            />
            <p className="text-xs text-gray-500 mt-1">
              Payment due N days after each flight or support-ticket utilization.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Statement Day (optional)
            </label>
            <select
              value={formData.payment_day}
              onChange={(e) => setFormData({ ...formData, payment_day: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="MONDAY">Monday</option>
              <option value="TUESDAY">Tuesday</option>
              <option value="WEDNESDAY">Wednesday</option>
              <option value="THURSDAY">Thursday</option>
              <option value="FRIDAY">Friday</option>
              <option value="SATURDAY">Saturday</option>
              <option value="SUNDAY">Sunday</option>
            </select>
          </div>

          {currentWallet > 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.transfer_wallet_to_credit}
                  onChange={(e) => setFormData({ ...formData, transfer_wallet_to_credit: e.target.checked })}
                  className="mt-1 w-4 h-4 text-blue-600 rounded"
                />
                <div className="text-sm">
                  <p className="font-semibold text-gray-800 mb-1">
                    Add wallet balance to credit limit (clears wallet)
                  </p>
                  <p className="text-gray-600">
                    Current wallet (₹{currentWallet.toLocaleString()}) moves into credit limit; wallet is zeroed.
                  </p>
                  {formData.transfer_wallet_to_credit && formData.credit_limit && (
                    <p className="text-blue-700 font-semibold mt-2">
                      Final Credit Limit: ₹{(parseInt(formData.credit_limit, 10) + currentWallet).toLocaleString()}
                    </p>
                  )}
                </div>
              </label>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Assigning...' : 'Assign Credit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignCreditModal;
